import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { TelemetryLapData, TrackParameters } from '../../models/TelemetryData';
import { parseTrackIni, getTrackAssetBaseName } from '../../utils/parseTrackIni';
import { getLapColor } from './LapSelector';
import { AlignMode, resolveDotDistance } from '../../utils/trackMapAlignment';
import { computeWindowTimes, findClosestDistanceForTime, findClosestTimeForDistance } from './trackMapPlayback';

/**
 * Compute heatmap color for a telemetry frame.
 * Brake (b, 0-100) → red intensity. Throttle (t, 0-100) → green intensity.
 * Brake wins when both are applied (trail braking).
 */
function heatmapColor(frame: { b: number; t: number }): string {
  const brk = frame.b / 100;
  const thr = frame.t / 100;

  // Minimum intensity so light pedal inputs are visually distinct from coast
  const MIN = 0.2;

  if (brk > 0) {
    const intensity = MIN + (1 - MIN) * brk;
    const r = Math.round(0x1a + (0xff - 0x1a) * intensity);
    const g = Math.round(0x1a + (0x11 - 0x1a) * intensity);
    const b = Math.round(0x1a + (0x11 - 0x1a) * intensity);
    return `rgb(${r},${g},${b})`;
  }
  if (thr > 0) {
    const intensity = MIN + (1 - MIN) * thr;
    const r = Math.round(0x1a + (0x11 - 0x1a) * intensity);
    const g = Math.round(0x1a + (0xcc - 0x1a) * intensity);
    const b = Math.round(0x1a + (0x11 - 0x1a) * intensity);
    return `rgb(${r},${g},${b})`;
  }
  return '#1a1a1a';
}

/**
 * Compute a point offset perpendicular to the line direction.
 * Returns [offsetX, offsetY] for the given pixel offset distance.
 */
function perpendicularOffset(
  x1: number, y1: number, x2: number, y2: number, offset: number
): [number, number] {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return [0, 0];
  // Perpendicular direction (rotated 90°)
  return [(-dy / len) * offset, (dx / len) * offset];
}

interface TrackMapProps {
  track: string;
  layout: string;
  lapDataMap: Map<string, TelemetryLapData>;
  checkedLapIds: string[];
  currentDistanceMarker: number | null;
  allLapIds?: string[];
  zoomRange?: { min: number; max: number } | null;
  heatmapEnabled?: boolean;
  onHeatmapToggle?: (enabled: boolean) => void;
  heatmapLapIds?: string[];
  playbackState?: { playing: boolean; time: number; speed: 1 | 2 | 4; loop: boolean };
  onPlaybackStateChange?: (state: { playing: boolean; time: number; speed: 1 | 2 | 4; loop: boolean }) => void;
  onPlaybackTick?: (distanceMarker: number) => void;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  alignMode: AlignMode;
  anchorLapId: string | null;
  onAlignModeChange: (mode: AlignMode) => void;
}

/** Parse a telemetry position string "(x, y, z)" → [x, y, z] or null if malformed. */
function parsePos(p: string): [number, number, number] | null {
  const parts = p.replace(/[()\s]/g, '').split(',');
  if (parts.length < 3) return null;
  const x = parseFloat(parts[0]);
  const y = parseFloat(parts[1]);
  const z = parseFloat(parts[2]);
  if (isNaN(x) || isNaN(y) || isNaN(z)) return null;
  return [x, y, z];
}

const TrackMap: React.FC<TrackMapProps> = ({
  track,
  layout,
  lapDataMap,
  checkedLapIds,
  currentDistanceMarker,
  allLapIds,
  zoomRange,
  heatmapEnabled,
  onHeatmapToggle,
  heatmapLapIds,
  playbackState,
  onPlaybackStateChange,
  onPlaybackTick,
  onFullscreenChange,
  alignMode,
  anchorLapId,
  onAlignModeChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [trackImage, setTrackImage] = useState<HTMLImageElement | null>(null);
  const [trackParams, setTrackParams] = useState<TrackParameters | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Notify parent when fullscreen state changes
  useEffect(() => {
    onFullscreenChange?.(isFullscreen);
  }, [isFullscreen, onFullscreenChange]);

  // Zoom/pan state
  const zoomLevelRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const lastPanRef = useRef({ x: 0, y: 0 });

  // Tracks the last zoom range we auto-fit to (by value, not object identity).
  // The chart emits a brand-new zoomRange object on every onZoom frame even when
  // the numbers are unchanged; without this guard the auto-fit effect refires and
  // clobbers any manual pan/zoom the user has applied, producing a "vibrating,
  // can't-move" track map. We also set this when the user pans/zooms manually so
  // an unchanged range never re-snaps the view back.
  const appliedZoomKeyRef = useRef<string | null>(null);

  // Load track image and INI
  useEffect(() => {
    setLoadError(false);
    setTrackImage(null);
    setTrackParams(null);

    if (!track || !layout) {
      setLoadError(true);
      return;
    }

    const baseName = getTrackAssetBaseName(track, layout);
    const imgUrl = `${process.env.PUBLIC_URL}/tracks/${baseName}.png`;
    const iniUrl = `${process.env.PUBLIC_URL}/tracks/${baseName}.txt`;

    let cancelled = false;

    const img = new Image();
    img.onload = () => {
      if (!cancelled) setTrackImage(img);
    };
    img.onerror = () => {
      if (!cancelled) setLoadError(true);
    };
    img.src = imgUrl;

    fetch(iniUrl)
      .then((res) => {
        if (!res.ok) throw new Error('INI not found');
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setTrackParams(parseTrackIni(text));
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [track, layout]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !trackImage || !trackParams) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(offsetRef.current.x, offsetRef.current.y);
    ctx.scale(zoomLevelRef.current, zoomLevelRef.current);

    // Fit track image to canvas preserving aspect ratio
    const imgAspect = trackImage.naturalWidth / trackImage.naturalHeight;
    const canvasAspect = canvas.width / canvas.height;
    let drawW: number, drawH: number, drawX: number, drawY: number;
    if (canvasAspect > imgAspect) {
      drawH = canvas.height;
      drawW = drawH * imgAspect;
      drawX = (canvas.width - drawW) / 2;
      drawY = 0;
    } else {
      drawW = canvas.width;
      drawH = drawW / imgAspect;
      drawX = 0;
      drawY = (canvas.height - drawH) / 2;
    }
    ctx.globalAlpha = 0.3;
    ctx.drawImage(trackImage, drawX, drawY, drawW, drawH);
    ctx.globalAlpha = 1.0;

    // The reference uses raw coords + offset in the image's native pixel space.
    // Scale those coords by (drawn size / natural size) to match the fitted image.
    const { xOffset, zOffset } = trackParams;
    const sx = drawW / trackImage.naturalWidth;
    const sy = drawH / trackImage.naturalHeight;

    // Draw lap lines
    const lapIdsToRender = heatmapEnabled && heatmapLapIds && heatmapLapIds.length > 0
      ? heatmapLapIds
      : checkedLapIds;

    lapIdsToRender.forEach((lapId, lapIndex) => {
      const telemetry = lapDataMap.get(lapId);
      if (!telemetry) return;

      const color = getLapColor(lapId, allLapIds || checkedLapIds);
      const frames = telemetry.t;
      const keys = Object.keys(frames);

      // Compute offset for heatmap dual lines: first lap inside (-), second outside (+)
      const offsetPx = heatmapEnabled && heatmapLapIds && heatmapLapIds.length === 2
        ? (lapIndex === 0 ? -3 : 3)
        : 0;

      if (heatmapEnabled) {
        // Draw per-segment colored line
        for (let i = 0; i < keys.length - 1; i++) {
          const frame = frames[keys[i]];
          const nextFrame = frames[keys[i + 1]];
          const pos = parsePos(frame.p);
          const nextPos = parsePos(nextFrame.p);
          if (!pos || !nextPos) continue;
          const x1 = drawX + (pos[0] + xOffset) * sx;
          const y1 = drawY + (pos[2] + zOffset) * sy;
          const x2 = drawX + (nextPos[0] + xOffset) * sx;
          const y2 = drawY + (nextPos[2] + zOffset) * sy;

          const [offX, offY] = perpendicularOffset(x1, y1, x2, y2, offsetPx);

          ctx.beginPath();
          ctx.moveTo(x1 + offX, y1 + offY);
          ctx.lineTo(x2 + offX, y2 + offY);
          ctx.strokeStyle = heatmapColor(frame);
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }
      } else {
        // Original: flat-color line
        ctx.beginPath();
        keys.forEach((key, i) => {
          const frame = frames[key];
          const pos = parsePos(frame.p);
          if (!pos) return; // skip this point
          const x = drawX + (pos[0] + xOffset) * sx;
          const y = drawY + (pos[2] + zOffset) * sy;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Position dot (same logic as before, but with offset for heatmap)
      if (currentDistanceMarker != null) {
        const findClosestKey = (ks: string[], dist: number) =>
          ks.reduce((prev, curr) =>
            Math.abs(parseFloat(curr) - dist) < Math.abs(parseFloat(prev) - dist) ? curr : prev
          );

        const anchorTelemetry = anchorLapId ? lapDataMap.get(anchorLapId) ?? null : null;
        const dotDistance = resolveDotDistance(
          alignMode,
          anchorTelemetry,
          telemetry,
          currentDistanceMarker
        );

        const closestKey = findClosestKey(keys, dotDistance);
        const frame = frames[closestKey];
        if (frame) {
          const pos = parsePos(frame.p);
          if (!pos) return; // skip dot for this lap
          let dotX = drawX + (pos[0] + xOffset) * sx;
          let dotY = drawY + (pos[2] + zOffset) * sy;

          // Apply same perpendicular offset so dots ride on their line
          if (offsetPx !== 0 && keys.indexOf(closestKey) < keys.length - 1) {
            const nextKey = keys[keys.indexOf(closestKey) + 1];
            const nextFrame = frames[nextKey];
            const nextPos = parsePos(nextFrame.p);
            if (nextPos) {
              const nx = drawX + (nextPos[0] + xOffset) * sx;
              const ny = drawY + (nextPos[2] + zOffset) * sy;
              const [offX, offY] = perpendicularOffset(dotX, dotY, nx, ny, offsetPx);
              dotX += offX;
              dotY += offY;
            }
          }

          ctx.beginPath();
          ctx.arc(dotX, dotY, 4, 0, 2 * Math.PI);
          ctx.fillStyle = color;
          ctx.fill();
        }
      }
    });

    ctx.restore();
  }, [trackImage, trackParams, lapDataMap, checkedLapIds, currentDistanceMarker, allLapIds, heatmapEnabled, heatmapLapIds, alignMode, anchorLapId]);

  // Auto-zoom track map when chart zoom range changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !trackImage || !trackParams) return;

    // Only auto-fit when the zoom range value actually changes. The chart emits a
    // fresh zoomRange object on every onZoom frame; reacting to object identity would
    // re-snap the view and fight manual pan/zoom (the "vibrate, can't move" bug).
    const zoomKey = zoomRange ? `${zoomRange.min}:${zoomRange.max}` : null;
    if (zoomKey === appliedZoomKeyRef.current) return;
    appliedZoomKeyRef.current = zoomKey;

    if (!zoomRange) {
      // Reset to default view
      zoomLevelRef.current = 1;
      offsetRef.current = { x: 0, y: 0 };
      draw();
      return;
    }

    // Compute canvas-space coords for the image fit
    const imgAspect = trackImage.naturalWidth / trackImage.naturalHeight;
    const canvasAspect = canvas.width / canvas.height;
    let drawW: number, drawH: number, drawX: number, drawY: number;
    if (canvasAspect > imgAspect) {
      drawH = canvas.height;
      drawW = drawH * imgAspect;
      drawX = (canvas.width - drawW) / 2;
      drawY = 0;
    } else {
      drawW = canvas.width;
      drawH = drawW / imgAspect;
      drawX = 0;
      drawY = (canvas.height - drawH) / 2;
    }
    const { xOffset, zOffset } = trackParams;
    const sx = drawW / trackImage.naturalWidth;
    const sy = drawH / trackImage.naturalHeight;

    // Find bounding box of all points within the zoom distance range
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    let pointCount = 0;

    checkedLapIds.forEach((lapId) => {
      const telemetry = lapDataMap.get(lapId);
      if (!telemetry) return;
      const frames = telemetry.t;

      Object.keys(frames).forEach((key) => {
        const dist = parseFloat(key);
        if (dist < zoomRange.min || dist > zoomRange.max) return;
        const frame = frames[key];
        const pos = parsePos(frame.p);
        if (!pos) return;
        const x = drawX + (pos[0] + xOffset) * sx;
        const y = drawY + (pos[2] + zOffset) * sy;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        pointCount++;
      });
    });

    if (pointCount < 2) return;

    // Add padding around the bounding box
    const pad = 30;
    minX -= pad; minY -= pad;
    maxX += pad; maxY += pad;

    const boxW = maxX - minX;
    const boxH = maxY - minY;
    const zoom = Math.min(canvas.width / boxW, canvas.height / boxH);

    // Center the bounding box in the canvas
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    zoomLevelRef.current = zoom;
    offsetRef.current = {
      x: canvas.width / 2 - centerX * zoom,
      y: canvas.height / 2 - centerY * zoom,
    };
    draw();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomRange, trackImage, trackParams, checkedLapIds, lapDataMap]);

  // Redraw when dependencies change
  useEffect(() => {
    draw();
  }, [draw]);

  // Resize canvas
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resizeObserver = new ResizeObserver(() => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      draw();
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [draw]);

  // Wheel zoom — use ref-based listener for { passive: false }
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const zoomStep = 0.1;
      const oldZoom = zoomLevelRef.current;
      const newZoom = e.deltaY < 0
        ? Math.min(oldZoom + zoomStep, 10)
        : Math.max(oldZoom - zoomStep, 0.5);

      const scaleFactor = newZoom / oldZoom;
      offsetRef.current.x = (offsetRef.current.x - mouseX) * scaleFactor + mouseX;
      offsetRef.current.y = (offsetRef.current.y - mouseY) * scaleFactor + mouseY;
      zoomLevelRef.current = newZoom;

      draw();
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [draw]);

  // The lap whose time defines the playback timeline and the dot time-anchor.
  // Use the selected anchor when present, else fall back to the first drawn lap.
  const baselineLapId = anchorLapId ?? (heatmapEnabled && heatmapLapIds?.length
    ? heatmapLapIds[0]
    : checkedLapIds[0]);

  // Compute max playback time from baseline lap
  const maxPlaybackTime = useMemo(() => {
    const bt = lapDataMap.get(baselineLapId);
    if (!bt) return 0;
    return Object.keys(bt.t).reduce((max, k) => Math.max(max, bt.t[k].m), -Infinity);
  }, [lapDataMap, baselineLapId]);

  // Playback window [start,end] in ms, derived from the chart zoom range (distance).
  const playbackWindow = useMemo(
    () => computeWindowTimes(lapDataMap.get(baselineLapId), zoomRange ?? null, maxPlaybackTime),
    [lapDataMap, baselineLapId, zoomRange, maxPlaybackTime]
  );

  // Refs so the running RAF loop always sees the latest window/loop values
  // without re-subscribing (and restarting startRealTime) every frame.
  const playbackWindowRef = useRef(playbackWindow);
  playbackWindowRef.current = playbackWindow;
  const loopRef = useRef(playbackState?.loop ?? true);
  loopRef.current = playbackState?.loop ?? true;

  // Playback animation loop
  useEffect(() => {
    if (!playbackState?.playing || !lapDataMap.size) return;

    const baselineTelemetry = lapDataMap.get(baselineLapId);
    if (!baselineTelemetry) return;

    const baseKeys = Object.keys(baselineTelemetry.t);
    const maxTime = baseKeys.reduce((max, k) => Math.max(max, baselineTelemetry.t[k].m), -Infinity);

    let startRealTime: number | null = null;
    let basePlaybackTime = playbackState.time; // segment base, re-anchored on each wrap
    let animFrame: number;

    const tick = (timestamp: number) => {
      if (startRealTime === null) startRealTime = timestamp;
      const elapsed = (timestamp - startRealTime) * (playbackState.speed || 1);
      const playbackTime = basePlaybackTime + elapsed;

      const winStart = playbackWindowRef.current.start;
      const winEnd = playbackWindowRef.current.end;

      if (winEnd <= winStart) {
        // Degenerate/zero-width window — nothing to play; stop cleanly.
        onPlaybackStateChange?.({ ...playbackState, loop: loopRef.current, playing: false, time: winStart });
        return;
      }

      if (playbackTime >= winEnd) {
        if (loopRef.current) {
          // Loop: wrap back to the window start and keep playing.
          const wrapped = winStart + (playbackTime - winEnd);
          const nextTime = wrapped >= winEnd ? winStart : wrapped;
          startRealTime = timestamp;
          basePlaybackTime = nextTime;
          onPlaybackStateChange?.({ ...playbackState, loop: loopRef.current, playing: true, time: nextTime });
          const bt = baselineTelemetry;
          onPlaybackTick?.(findClosestDistanceForTime(bt, nextTime));
          playbackTimeRef.current = nextTime;
          const pct = maxTime > 0 ? (nextTime / maxTime) * 100 : 0;
          if (scrubberFillRef.current) scrubberFillRef.current.style.width = `${pct}%`;
          if (scrubberHandleRef.current) scrubberHandleRef.current.style.left = `${pct}%`;
          animFrame = requestAnimationFrame(tick);
          return;
        }
        // Loop off: pause at the window end.
        onPlaybackStateChange?.({ ...playbackState, loop: loopRef.current, playing: false, time: winEnd });
        onPlaybackTick?.(findClosestDistanceForTime(baselineTelemetry, winEnd));
        const pct = maxTime > 0 ? (winEnd / maxTime) * 100 : 0;
        setScrubberPct(pct);
        return;
      }

      // Find the distance at this playback time
      let closestDist = 0;
      let closestDiff = Infinity;
      baseKeys.forEach((k) => {
        const diff = Math.abs(baselineTelemetry.t[k].m - playbackTime);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestDist = parseFloat(k);
        }
      });

      onPlaybackTick?.(closestDist);
      playbackTimeRef.current = playbackTime;

      // Update scrubber via DOM refs (avoids React re-render per frame)
      const pct = maxTime > 0 ? (playbackTime / maxTime) * 100 : 0;
      if (scrubberFillRef.current) scrubberFillRef.current.style.width = `${pct}%`;
      if (scrubberHandleRef.current) scrubberHandleRef.current.style.left = `${pct}%`;

      animFrame = requestAnimationFrame(tick);
    };

    animFrame = requestAnimationFrame(tick);

    return () => {
      if (animFrame) cancelAnimationFrame(animFrame);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackState?.playing, playbackState?.speed, baselineLapId]);

  // Mouse event handlers for pan
  const handleMouseDown = (e: React.MouseEvent) => {
    isPanningRef.current = true;
    lastPanRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - lastPanRef.current.x;
    const dy = e.clientY - lastPanRef.current.y;
    offsetRef.current.x += dx;
    offsetRef.current.y += dy;
    lastPanRef.current = { x: e.clientX, y: e.clientY };
    draw();
  };

  const handleMouseUp = () => {
    isPanningRef.current = false;
  };

  // Double-click to reset
  const handleDoubleClick = () => {
    zoomLevelRef.current = 1;
    offsetRef.current = { x: 0, y: 0 };
    draw();
  };

  // Clamp the playhead into the window whenever a zoom commit leaves it outside.
  // Runs on window change (i.e. new zoomRange), not on every playback tick.
  useEffect(() => {
    // Only clamp in TIME mode. In DIST mode the playback controls are hidden, but a
    // distance-chart zoom still flows a zoomRange in here; clamping there would reset
    // playback state and move the dot, changing DIST-mode behavior.
    if (alignMode !== 'time') return;
    if (!playbackState) return;
    if (playbackState.time < playbackWindow.start || playbackState.time > playbackWindow.end) {
      onPlaybackStateChange?.({ ...playbackState, playing: false, time: playbackWindow.start });
      const bt = lapDataMap.get(baselineLapId);
      if (bt) onPlaybackTick?.(findClosestDistanceForTime(bt, playbackWindow.start));
    }
    // Depend on the window bounds (and alignMode) only — not on playbackState.time — so a
    // normal playback tick (which advances time) does not re-trigger the clamp, while
    // switching INTO time mode with an out-of-window playhead still clamps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackWindow.start, playbackWindow.end, alignMode]);

  // Scrubber progress — ref for smooth DOM updates, state for initial render
  const scrubberFillRef = useRef<HTMLDivElement>(null);
  const scrubberHandleRef = useRef<HTMLDivElement>(null);
  const [scrubberPct, setScrubberPct] = useState(0);

  // Whether a pointer drag on the scrubber is currently in progress.
  const isScrubbingRef = useRef(false);

  // Compute the playback time for a pointer clientX along the scrubber track,
  // update the fill/handle DOM + scrubberPct, and drive the chart cursor/track
  // dot live via onPlaybackTick. Returns the resulting time (ms) so callers can
  // commit it via onPlaybackStateChange.
  const seekToClientX = useCallback((clientX: number, scrubberEl: HTMLElement) => {
    const rect = scrubberEl.getBoundingClientRect();
    const rawPct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const rawTime = rawPct * maxPlaybackTime;
    // Clamp to the active playback window (full lap when not zoomed).
    const newTime = Math.max(playbackWindow.start, Math.min(playbackWindow.end, rawTime));
    const displayPct = maxPlaybackTime > 0 ? (newTime / maxPlaybackTime) * 100 : 0;
    setScrubberPct(displayPct);
    if (scrubberFillRef.current) scrubberFillRef.current.style.width = `${displayPct}%`;
    if (scrubberHandleRef.current) scrubberHandleRef.current.style.left = `${displayPct}%`;
    const bt = lapDataMap.get(baselineLapId);
    if (bt) onPlaybackTick?.(findClosestDistanceForTime(bt, newTime));
    return newTime;
  }, [maxPlaybackTime, playbackWindow.start, playbackWindow.end, lapDataMap, baselineLapId, onPlaybackTick]);

  // Keep the scrubber position in sync with the shared playhead (currentDistanceMarker),
  // which changes from sources OTHER than playback/drag: clicking a chart trace, the
  // zoom-commit clamp jumping the playhead, arrow-key nav, etc. Without this, the track
  // dot moves but the scrubber handle stays put. Skipped while a drag is in progress
  // (the drag drives the DOM directly) and while playing (the RAF loop drives it).
  useEffect(() => {
    if (isScrubbingRef.current || playbackState?.playing) return;
    if (currentDistanceMarker == null || maxPlaybackTime <= 0) return;
    const bt = lapDataMap.get(baselineLapId);
    if (!bt) return;
    const timeMs = findClosestTimeForDistance(bt, currentDistanceMarker);
    const pct = (timeMs / maxPlaybackTime) * 100;
    setScrubberPct(pct);
    if (scrubberFillRef.current) scrubberFillRef.current.style.width = `${pct}%`;
    if (scrubberHandleRef.current) scrubberHandleRef.current.style.left = `${pct}%`;
  }, [currentDistanceMarker, maxPlaybackTime, lapDataMap, baselineLapId, playbackState?.playing]);

  // Track current playback time via ref so speed changes can snapshot it
  const playbackTimeRef = useRef(0);

  // Escape key closes fullscreen and stops playback
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) setIsFullscreen(false);
        if (playbackState?.playing) {
          onPlaybackStateChange?.({ ...(playbackState || { speed: 1 as const, time: 0, loop: false }), playing: false, time: 0 });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, playbackState, onPlaybackStateChange]);

  if (loadError) {
    return null; // Track map not available — component returns nothing, parent can expand charts
  }

  return (
    <div className={`tele-track-map${isFullscreen ? ' track-map-fullscreen' : ''}`}>
      <button
        className="tele-expand-btn"
        onClick={() => setIsFullscreen(!isFullscreen)}
        title={isFullscreen ? 'Close fullscreen' : 'Expand to fullscreen'}
      >
        {isFullscreen ? '\u2715' : '\u26F6'}
      </button>

      {/* Heatmap legend — visible only when heatmap is on */}
      {heatmapEnabled && (
        <div className="track-map-legend">
          <span className="track-map-legend-label">BRK</span>
          <div className="track-map-legend-gradient" />
          <span className="track-map-legend-label">THR</span>
        </div>
      )}

      <div className="tele-track-map-canvas" ref={containerRef}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
        />
        {/* Canvas-interaction hint lives inside the canvas area so it anchors to
            the bottom of the map (above the overlay), never overlapping the
            TIME/DIST/HEATMAP row below. */}
        <div className="track-map-hint">
          Scroll to zoom &bull; Drag to pan &bull; {isFullscreen ? 'Esc to close' : 'Double-click to reset'}
        </div>
      </div>

      {/* Overlay stack: timeline row, then modes row */}
      <div className="track-map-overlay">
        {/* Section 1 \u2014 playback timeline. Present in both modes; disabled in DIST
            (playback only applies in TIME mode) so the card height stays constant. */}
        <div className={`track-map-timeline${alignMode !== 'time' ? ' disabled' : ''}`} aria-disabled={alignMode !== 'time'}>
          <button
            className="track-map-play-btn"
            aria-label={playbackState?.playing ? 'Pause' : 'Play'}
            disabled={alignMode !== 'time'}
            onClick={() => {
              if (!playbackState) return;
              const newPlaying = !playbackState.playing;
              // On play, resume from the visible playhead. currentDistanceMarker is
              // the authoritative playhead — a trace click / arrow-key nav moves it
              // (and the dot) but does NOT update playbackState.time, so trusting
              // playbackState.time here would replay from a stale point (the lap
              // restart bug). Derive the start time from the marker instead.
              const bt = lapDataMap.get(baselineLapId);
              const markerTime = newPlaying && bt && currentDistanceMarker != null
                ? findClosestTimeForDistance(bt, currentDistanceMarker)
                : playbackState.time;
              const outOfRange = markerTime >= playbackWindow.end || markerTime < playbackWindow.start;
              onPlaybackStateChange?.({
                ...playbackState,
                playing: newPlaying,
                time: newPlaying && outOfRange ? playbackWindow.start : markerTime,
              });
            }}
          >
            {playbackState?.playing ? '\u275A\u275A' : '\u25B6'}
          </button>
          <button
            className="track-map-restart-btn"
            aria-label="Restart"
            title="Restart from section start"
            disabled={alignMode !== 'time'}
            onClick={() => {
              const bt = lapDataMap.get(baselineLapId);
              onPlaybackStateChange?.({
                ...(playbackState || { speed: 1 as const, loop: true }),
                playing: true,
                time: playbackWindow.start,
              });
              if (bt) onPlaybackTick?.(findClosestDistanceForTime(bt, playbackWindow.start));
            }}
          >
            {'\u21BA'}
          </button>
          <button
            className={`track-map-loop-btn${playbackState?.loop ? ' active' : ''}`}
            aria-label="Toggle loop"
            aria-pressed={playbackState?.loop ? true : false}
            title={playbackState?.loop ? 'Loop on' : 'Loop off'}
            disabled={alignMode !== 'time'}
            onClick={() => {
              onPlaybackStateChange?.({
                ...(playbackState || { playing: false, time: 0, speed: 1 as const }),
                loop: !(playbackState?.loop ?? true),
              });
            }}
          >
            {'\u27F2'}
          </button>
          <div
            className="track-map-scrubber"
            onPointerDown={(e) => {
              if (alignMode !== 'time') return;
              isScrubbingRef.current = true;
              (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
              // Pause while scrubbing (matches click-to-seek behavior).
              onPlaybackStateChange?.({ ...(playbackState || { speed: 1 as const, loop: true }), playing: false, time: seekToClientX(e.clientX, e.currentTarget as HTMLElement) });
            }}
            onPointerMove={(e) => {
              if (!isScrubbingRef.current) return;
              seekToClientX(e.clientX, e.currentTarget as HTMLElement);
            }}
            onPointerUp={(e) => {
              if (!isScrubbingRef.current) return;
              isScrubbingRef.current = false;
              (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
              const newTime = seekToClientX(e.clientX, e.currentTarget as HTMLElement);
              onPlaybackStateChange?.({ ...(playbackState || { speed: 1 as const, loop: true }), playing: false, time: newTime });
            }}
          >
            {zoomRange && maxPlaybackTime > 0 && (
              <div
                className="track-map-scrubber-band"
                style={{
                  left: `${(playbackWindow.start / maxPlaybackTime) * 100}%`,
                  width: `${((playbackWindow.end - playbackWindow.start) / maxPlaybackTime) * 100}%`,
                }}
              />
            )}
            <div className="track-map-scrubber-fill" ref={scrubberFillRef} style={{ width: `${scrubberPct}%` }} />
            <div className="track-map-scrubber-handle" ref={scrubberHandleRef} style={{ left: `${scrubberPct}%` }} />
          </div>
          <button
            className="track-map-speed-btn"
            disabled={alignMode !== 'time'}
            onClick={() => {
              const speeds: Array<1 | 2 | 4> = [1, 2, 4];
              const idx = speeds.indexOf(playbackState?.speed || 1);
              const nextSpeed = speeds[(idx + 1) % speeds.length];
              // Snapshot current playback time so the animation resumes from here at the new speed
              const currentTime = playbackState?.playing ? playbackTimeRef.current : (playbackState?.time || 0);
              onPlaybackStateChange?.({ ...(playbackState || { playing: false, time: 0, loop: false }), speed: nextSpeed, time: currentTime });
            }}
          >
            {playbackState?.speed || 1}x
          </button>
        </div>

        {/* Section 2 \u2014 align mode + heatmap toggles */}
        <div className="track-map-modes">
          <button
            className={`track-map-chip${alignMode === 'time' ? ' active' : ''}`}
            onClick={() => onAlignModeChange('time')}
          >TIME</button>
          <button
            className={`track-map-chip${alignMode === 'dist' ? ' active' : ''}`}
            onClick={() => onAlignModeChange('dist')}
          >DIST</button>
          <span className="track-map-overlay-divider" />
          <span className="track-map-overlay-label">HEATMAP</span>
          <button
            className={`track-map-chip${!heatmapEnabled ? ' active' : ''}`}
            onClick={() => onHeatmapToggle?.(false)}
          >OFF</button>
          <button
            className={`track-map-chip${heatmapEnabled ? ' active' : ''}`}
            onClick={() => onHeatmapToggle?.(true)}
          >ON</button>
        </div>
      </div>
    </div>
  );
};

export default TrackMap;
